import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getBlockDetail } from "@/lib/api";
import { BLOCK } from "@/constants/testIds";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Blocks, ArrowLeft, Clock, Hash, User, Fuel } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";

function formatTimestamp(ts) {
  return new Date(ts * 1000).toLocaleString();
}

function truncate(str, start = 8, end = 6) {
  if (!str || str.length <= start + end) return str;
  return `${str.slice(0, start)}...${str.slice(-end)}`;
}

function formatFMI(weiStr) {
  return (Number(weiStr) / 1e18).toFixed(4);
}

export default function BlockDetail() {
  const { number } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["block", number],
    queryFn: () => getBlockDetail(Number(number)),
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-gold-pulse text-center text-[#D4AF37] py-20 font-display">Loading block...</div>
      </div>
    );
  }

  const block = data?.block;
  const txs = data?.transactions || [];
  const isDemo = data?.isDemo;

  if (!block) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-center text-zinc-400">Block not found</div>
    );
  }

  const details = [
    { label: "Block Hash", value: block.hash, icon: Hash, mono: true, testId: BLOCK.hash },
    { label: "Parent Hash", value: block.parentHash, icon: Hash, mono: true },
    { label: "Timestamp", value: formatTimestamp(block.timestamp), icon: Clock, testId: BLOCK.timestamp },
    { label: "Miner", value: block.miner, icon: User, mono: true, link: `/address/${block.miner}`, testId: BLOCK.miner },
    { label: "Transactions", value: block.transactionCount, icon: Blocks, testId: BLOCK.txCount },
    { label: "Gas Used", value: `${block.gasUsed?.toLocaleString()} / ${block.gasLimit?.toLocaleString()}`, icon: Fuel },
    { label: "Size", value: `${block.size?.toLocaleString()} bytes` },
  ];

  return (
    <div data-testid={BLOCK.container} className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <motion.div {...FADE_UP}>
        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            Block <span data-testid={BLOCK.number} className="text-[#D4AF37]">#{block.number?.toLocaleString()}</span>
          </h1>
          {isDemo && <Badge className="demo-badge">Demo</Badge>}
        </div>

        <Card className="border-[#2A2411] bg-[#0A0A0A]">
          <CardContent className="p-6 space-y-4">
            {details.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={d.label}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <div className="flex items-center gap-2 min-w-[160px] text-zinc-500 text-sm">
                      {Icon && <Icon size={14} />}
                      {d.label}:
                    </div>
                    <div data-testid={d.testId} className={`text-sm break-all ${d.mono ? "font-mono" : ""} text-white`}>
                      {d.link ? (
                        <Link to={d.link} className="table-link">{d.value}</Link>
                      ) : (
                        d.value
                      )}
                    </div>
                  </div>
                  {i < details.length - 1 && <Separator className="mt-4 bg-[#2A2411]" />}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Block Transactions */}
        {txs.length > 0 && (
          <Card className="border-[#2A2411] bg-[#0A0A0A] mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Transactions ({txs.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table data-testid={BLOCK.txTable}>
                <TableHeader>
                  <TableRow className="border-[#2A2411] hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-xs">Tx Hash</TableHead>
                    <TableHead className="text-zinc-500 text-xs">From</TableHead>
                    <TableHead className="text-zinc-500 text-xs">To</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txs.map((tx, i) => (
                    <TableRow key={tx.hash} className="border-[#2A2411] stagger-item" style={{ animationDelay: `${i * 30}ms` }}>
                      <TableCell>
                        <Link to={`/tx/${tx.hash}`} className="table-link font-mono text-xs">{truncate(tx.hash)}</Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/address/${tx.from}`} className="table-link font-mono text-xs">{truncate(tx.from, 6, 4)}</Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/address/${tx.to}`} className="table-link font-mono text-xs">{truncate(tx.to, 6, 4)}</Link>
                      </TableCell>
                      <TableCell className="text-right text-zinc-300 font-mono text-xs">{formatFMI(tx.value)} FMI</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
